import React, { FC, useState } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import ScienceIcon from '@mui/icons-material/Science'
import DeleteConfirmWindow from '../system/DeleteConfirmWindow'
import ThemeEditModal from './ThemeEditModal'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import AutoThemeModal, { AutoThemeConfig } from './AutoThemeModal'
import { AutoCoderCode } from '../../types/gotypes'

interface ThemeEditorProps {
  themes: AutoCoderCode[]
  isLoading?: boolean
  error?: string
  colorMap?: Record<string, string>
  readOnly?: boolean
  onAddTheme: (theme: AutoCoderCode) => void
  onUpdateTheme: (previousCode: string, theme: AutoCoderCode) => void
  onDeleteTheme: (code: string) => void
  onAutoTheme?: (config: AutoThemeConfig) => void
  onTest?: () => void
}

interface ThemeEditorState {
  open: boolean
  mode: 'add' | 'edit'
  targetTheme?: AutoCoderCode
}

interface DeleteState {
  open: boolean
  targetTheme?: AutoCoderCode
}

interface AutoThemeModalState {
  open: boolean
}

const ThemeEditor: FC<ThemeEditorProps> = ({
  themes,
  isLoading,
  error,
  colorMap,
  readOnly = false,
  onAddTheme,
  onUpdateTheme,
  onDeleteTheme,
  onAutoTheme,
  onTest,
}) => {
  const [themeEditor, setThemeEditor] = useState<ThemeEditorState>({
    open: false,
    mode: 'add',
  })

  const [deleteState, setDeleteState] = useState<DeleteState>({
    open: false,
  })

  const [autoThemeModal, setAutoThemeModal] = useState<AutoThemeModalState>({
    open: false,
  })

  const handleOpenAdd = () => {
    setThemeEditor({
      open: true,
      mode: 'add',
      targetTheme: undefined,
    })
  }

  const handleOpenEdit = (theme: AutoCoderCode) => {
    setThemeEditor({
      open: true,
      mode: 'edit',
      targetTheme: theme,
    })
  }

  const handleSaveTheme = (theme: AutoCoderCode) => {
    if (themeEditor.mode === 'edit' && themeEditor.targetTheme) {
      onUpdateTheme(themeEditor.targetTheme.code, theme)
    } else {
      onAddTheme(theme)
    }
    setThemeEditor(prev => ({ ...prev, open: false }))
  }

  const handleRequestDelete = (theme: AutoCoderCode) => {
    setDeleteState({
      open: true,
      targetTheme: theme,
    })
  }

  const handleConfirmDelete = () => {
    if (!deleteState.targetTheme) {
      return
    }
    onDeleteTheme(deleteState.targetTheme.code)
    setDeleteState({ open: false })
  }

  const handleOpenAutoTheme = () => {
    setAutoThemeModal({ open: true })
  }

  const handleAutoThemeSubmit = (config: AutoThemeConfig) => {
    if (onAutoTheme) {
      onAutoTheme(config)
    }
    setAutoThemeModal({ open: false })
  }

  const defaultThemeCount = themes.length === 0 ? 10 : themes.length

  const hasThemes = themes.length > 0

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: 2,
        gap: 2,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <Typography variant='h6' sx={{ flex: 1 }}>Themes</Typography>
        </Box>
        {onTest && !readOnly ? (
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 0 }}>
            <Tooltip title='Test Themes'>
              <IconButton
                size='small'
                sx={{
                  ml: 'auto',
                  border: '1px solid #00B2FF',
                  color: '#00B2FF',
                  '&:hover': {
                    backgroundColor: '#00B2FF',
                    color: '#ffffff',
                  },
                }}
                onClick={onTest}
              >
                <ScienceIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </Box>
        ) : null}

        {onAutoTheme && !readOnly ? (
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 0 }}>
            <Tooltip title='Auto Extract Themes'>
              <IconButton
                size='small'
                sx={{
                  ml: 'auto',
                  backgroundColor: '#00B2FF',
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: '#0082AF',
                  },
                }}
                onClick={handleOpenAutoTheme}
              >
                <AutoAwesomeIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </Box>
        ) : null}

      </Box>

      {error ? (
        <Alert severity='error'>{error}</Alert>
      ) : null}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : null}

      {!isLoading && !hasThemes ? (
        <Typography variant='body2' color='text.secondary'>Ask to extract themes from the data or manually add themes.</Typography>
      ) : null}

      {hasThemes ? (
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
          {themes.map(theme => {
            const trimmed = theme.text?.trim() || theme.code
            const themeColor = (() => {
              if (!trimmed) return undefined
              if (!colorMap) return undefined
              return colorMap[trimmed] || undefined
            })()
            const indicatorColor = themeColor || '#CBD5F5'
            return (
              <Paper
                key={theme.code}
                variant='outlined'
                sx={{
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  borderLeft: themeColor ? `4px solid ${themeColor}` : undefined,
                  borderLeftWidth: themeColor ? 4 : undefined,
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: indicatorColor,
                    mt: 0.5,
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant='body2'
                    sx={{
                      fontSize: '0.75rem',
                      lineHeight: 1.2,
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      fontWeight: 500,
                    }}
                  >
                    {theme.text || theme.code}
                  </Typography>
                  {theme.description && (
                    <Typography
                      variant='caption'
                      sx={{
                        display: 'block',
                        color: 'text.secondary',
                        fontSize: '0.65rem',
                        lineHeight: 1.3,
                        mt: 0.25,
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                      }}
                    >
                      {theme.description}
                    </Typography>
                  )}
                </Box>
                {!readOnly && (
                  <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, flexShrink: 0 }}>
                    <IconButton size='small' onClick={() => handleRequestDelete(theme)}>
                      <DeleteIcon fontSize='small' />
                    </IconButton>
                    <IconButton size='small' onClick={() => handleOpenEdit(theme)}>
                      <EditIcon fontSize='small' />
                    </IconButton>
                  </Box>
                )}
              </Paper>
            )
          })}
        </Box>
      ) : null}

      {/* Adding comes after the list, where the eye finishes reading it (Matt, 21 Sep: "add theme should be at the bottom … there should be some kind of help"). */}
      {!readOnly && !isLoading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
          <Button color='info' startIcon={<AddIcon />} onClick={handleOpenAdd} sx={{ ml: -1 }}>
            Add a theme
          </Button>
          <Typography variant='caption' color='text.secondary'>
            Use the pencil to rename a theme or change its rule. The rule tells the model when to assign it.
          </Typography>
        </Box>
      )}

      <ThemeEditModal
        open={themeEditor.open}
        mode={themeEditor.mode}
        initialTheme={themeEditor.targetTheme}
        onCancel={() => setThemeEditor(prev => ({ ...prev, open: false }))}
        onSave={handleSaveTheme}
      />

      <DeleteConfirmWindow
        open={deleteState.open}
        title='Delete Theme'
        message={deleteState.targetTheme ? `Do you want to remove "${deleteState.targetTheme.text || deleteState.targetTheme.code}" from the list.` : 'Do you want to remove this theme from the list.'}
        confirmWord=''
        onCancel={() => setDeleteState({ open: false })}
        onConfirm={handleConfirmDelete}
      />

      <AutoThemeModal
        open={autoThemeModal.open}
        defaultThemeCount={defaultThemeCount}
        onCancel={() => setAutoThemeModal({ open: false })}
        onSubmit={handleAutoThemeSubmit}
      />
    </Box>
  )
}

export default ThemeEditor
